import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AppStateService } from '../services/app-state.service';
import { AuthOAuthService } from '../services/oauth.service';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

/**
 * HTTP Interceptor for adding authentication and authorization headers
 *
 * This interceptor adds the following headers to all HTTP requests:
 * 1. user-id: UUID of the authenticated user from LMS backend
 * 2. role: User's role (TRAINER, LEARNER, ADMIN) from AppStateService
 * 3. Authorization: Bearer token from OAuth for API authentication
 *
 * These headers are required by the backend API for:
 * - User identification and audit logging
 * - Role-based access control (RBAC)
 * - OAuth token validation
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Skip JWKS requests entirely to avoid CORS errors
  // JWKS validation is disabled at the service level anyway (It will be handled in the Backend)
  if (req.url.includes('jwks') || req.url.includes('/.well-known/')) {
    // console.debug('[AUTH] Skipping auth headers for JWKS/discovery request:', req.url);
    // Pass through without adding auth headers
    return next(req).pipe(
      catchError((error) => {
        // Suppress CORS errors for JWKS/discovery endpoints
        if (error.status === 0 || error.error?.type === 'Network error') {
          console.warn('[AUTH] CORS error for JWKS/discovery (suppressed):', error);
          // Return empty response instead of throwing error
          return of(error);
        }
        // Re-throw other errors
        throw error;
      })
    );
  }

  // Inject the services
  const appStateService = inject(AppStateService);
  const oauthService = inject(AuthOAuthService);

  // Get user ID from AppStateService (UUID from LMS backend)
  const currentUser = appStateService.currentUser();
  const userId = currentUser?.userId ?? '';

  // Get user role from app state service
  const userRole = appStateService.userRole();

  // Get OAuth access token if available
  const accessToken = oauthService.getAccessToken();

  // Build headers object with required authentication headers
  const headers: Record<string, string> = {};

  // Only add user-id if we have a valid UUID
  if (userId) {
    headers['user-id'] = userId;
  }

  // Only add role if we have a valid role
  if (userRole && userRole.trim()) {
    headers['role'] = userRole.toLocaleUpperCase();
  }

  // Add Authorization header with Bearer token if available
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  // Clone the request and add headers
  const modifiedReq = req.clone({
    setHeaders: headers
  });

  // Pass the modified request to the next handler
  return next(modifiedReq);
};